import React, { useMemo, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

/**
 * Setup Assistant bulk roster card (import_roster): one row per person with
 * a checkbox, welcome settings, and a single confirm. The rows come from the
 * proposal summary; confirm sends only exclusions and welcome overrides.
 */
export default function RosterProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const summary = proposal.summary || {};
  const rows = useMemo(() => (Array.isArray(summary.rows) ? summary.rows : []), [summary.rows]);
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loadingAction, setLoadingAction] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set(rows.filter((r) => r.blocked).map((r) => r.row_key)));
  const [sendWelcome, setSendWelcome] = useState(summary.send_welcome !== false);
  const [welcomeChannel, setWelcomeChannel] = useState(summary.welcome_channel === "sms" ? "sms" : "email");
  const [result, setResult] = useState(null);

  const selectedRows = rows.filter((r) => !r.blocked && !excluded.has(r.row_key));
  const selectedCount = selectedRows.length;
  // Texts can only go to selected people whose number can receive SMS; the
  // proposal's with_phone_count is the fallback for older proposals.
  const withPhone = rows.some((r) => "sms_capable" in r)
    ? selectedRows.filter((r) => r.sms_capable).length
    : Number(summary.with_phone_count) || 0;
  React.useEffect(() => {
    if (welcomeChannel === "sms" && withPhone === 0) setWelcomeChannel("email");
  }, [welcomeChannel, withPhone]);
  const storeName = summary.store_name || "";

  React.useEffect(() => {
    if ((proposal.status === "cancelled" || proposal.status === "rejected") && status === "pending") {
      setStatus("cancelled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.status]);

  const toggleRow = (rowKey) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleAction = async (action) => {
    setLoadingAction(action);
    try {
      const payload = {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (action === "confirm") {
        payload.overrides = {
          excluded_row_keys: rows.filter((r) => excluded.has(r.row_key)).map((r) => r.row_key),
          send_welcome: sendWelcome,
          welcome_channel: welcomeChannel,
        };
      }
      const res = await postRightworkAPI(confirmUrl, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        const executorResult = res?.data?.redux?.payload?.result;
        setResult(executorResult && typeof executorResult === "object" ? executorResult : null);
        setStatus(newStatus);
        // The welcome choice rides along so the panel can skip the invite step
        // when welcomes were deliberately turned off.
        onStatusChange?.(proposal.proposal_id, newStatus, { send_welcome: sendWelcome });
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoadingAction(null);
  };

  if (status === "confirmed") {
    const skipped = Number(result?.skipped_count) || 0;
    const tone = skipped > 0
      ? "bg-amber-50 border-amber-200 text-amber-800"
      : "bg-green-50 border-green-200 text-green-700";
    return (
      <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 border ${tone}`}>
        <CheckCircleIcon fontSize="small" className={`mt-0.5 shrink-0 ${skipped > 0 ? "text-amber-600" : "text-green-600"}`} />
        <div className="min-w-0">
          <div className="font-medium">{summary.title || "Team added"}</div>
          {result?.note && <div className="text-xs mt-1">{result.note}</div>}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <GroupAddIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Add {selectedCount} {selectedCount === 1 ? "person" : "people"}{storeName ? ` to ${storeName}` : ""}
        </span>
      </div>

      <div className="px-3 py-3 space-y-3">
        {summary.file_name && (
          <div className="text-xs text-gray-500">From {summary.file_name}. Uncheck anyone to leave out.</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pr-2 py-1" />
                <th className="pr-2 py-1">Name</th>
                <th className="pr-2 py-1">Email</th>
                <th className="pr-2 py-1">Role</th>
                <th className="pr-2 py-1">Access</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const checked = !row.blocked && !excluded.has(row.row_key);
                return (
                  <tr key={row.row_key} className={row.blocked ? "text-gray-400" : "text-gray-800"}>
                    <td className="pr-2 py-1 align-top">
                      <input
                        type="checkbox"
                        aria-label={`Include ${row.name || row.email}`}
                        className="accent-violet-700"
                        checked={checked}
                        disabled={row.blocked || loadingAction !== null}
                        onChange={() => toggleRow(row.row_key)}
                      />
                    </td>
                    <td className="pr-2 py-1 align-top">
                      <div>{row.name || "Unnamed"}</div>
                      {!row.blocked && row.warning && (
                        <div className="text-[11px] text-amber-700">{row.warning}</div>
                      )}
                      {row.blocked && row.reason && (
                        <div className="text-[11px] text-amber-700">{row.reason}</div>
                      )}
                    </td>
                    <td className="pr-2 py-1 align-top break-all">{row.email}</td>
                    <td className="pr-2 py-1 align-top">
                      {row.role_name || "None"}
                      {row.role_name && !row.has_role && (
                        <span className="text-[11px] text-amber-700"> (no matching role)</span>
                      )}
                    </td>
                    <td className="pr-2 py-1 align-top">{row.permission_label || "Employee"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {Array.isArray(summary.unknown_roles) && summary.unknown_roles.length > 0 && (
          <div className="text-xs text-amber-700">
            Roles not in Rightwork yet: {summary.unknown_roles.join(", ")}. Those people are added without a role.
          </div>
        )}
        {Array.isArray(summary.ambiguous_roles) && summary.ambiguous_roles.length > 0 && (
          <div className="text-xs text-amber-700">
            Several roles share the name {summary.ambiguous_roles.join(", ")}. Those people are added without a role; ask Lumi to assign one by department.
          </div>
        )}
        {summary.note && <div className="text-xs text-gray-600">{summary.note}</div>}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              className="accent-violet-700"
              checked={sendWelcome}
              disabled={loadingAction !== null}
              onChange={(e) => setSendWelcome(e.target.checked)}
            />
            <span>Send welcome messages</span>
          </label>
          <select
            aria-label="Welcome channel"
            value={welcomeChannel}
            disabled={!sendWelcome || loadingAction !== null}
            onChange={(e) => setWelcomeChannel(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
          >
            <option value="email">Email</option>
            <option value="sms" disabled={withPhone === 0}>
              Text message{withPhone === 0 ? " (no phone numbers)" : ""}
            </option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={() => handleAction("reject")}
          disabled={loadingAction !== null}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleAction("confirm")}
          disabled={loadingAction !== null || selectedCount === 0}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "confirm" && <CircularProgress size={14} sx={{ color: "white" }} />}
          Add to team
        </button>
      </div>
    </div>
  );
}
